import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useHubAppCopy } from '../../hooks';
import { api } from '../../utils/apiClient';
import OnboardingForm from './OnboardingForm';
import { OnboardingFormData } from './types';

export const OnboardingModal: React.FC = () => {
  const { user, isLoading, updateUser } = useAuth();
  const location = useLocation();
  const hubAppCopy = useHubAppCopy();
  const copy = hubAppCopy('portalCopy')?.onboarding;
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if modal should be displayed
  const shouldShowModal = useMemo(() => {
    // Don't show if still loading or no user
    if (isLoading || !user) return false;

    // Don't show if user requires subscription (they need to subscribe first)
    // Note: requiresSubscription is false for license users, so they CAN see this modal
    if (user.requiresSubscription) return false;

    // Don't interrupt subscription or checkout-confirmation flows — onboarding
    // should fire only once the user actually lands on the dashboard.
    if (location.pathname.startsWith('/subscription')) return false;
    if (location.pathname.startsWith('/checkout')) return false;

    // Show if any required field is missing (works for both direct and license users)
    const missingFields = !user.firstName || !user.lastName ||
                         !user.title || !user.preferredLocale;

    return missingFields;
  }, [user, isLoading, location]);

  useEffect(() => {
    setIsOpen(shouldShowModal);
  }, [shouldShowModal]);

  const handleSubmit = async (formData: OnboardingFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Update user profile via API
      await api.updateUserProfile(user!.id, formData);

      // Close modal on success - user context will update on next render/fetch
      setIsOpen(false);

      // Optionally reload the page to refresh user data
      window.location.reload();
    } catch (err) {
      console.error('Onboarding submission failed:', err);
      setError(copy?.errorMessage || 'There was an error updating your profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="cert-modal-overlay">
      <div className="cert-modal">
        <div className="cert-modal__header">
          {/* No close button - user must complete the form */}
        </div>

        <div className="cert-modal__body">
          <h2 className="cert-modal__title">{copy?.title || 'Get Started'}</h2>

          <p className="cert-modal__description">
            {copy?.description || 'Create your Prosci Portal account to begin your change management experience.'}
          </p>

          {error && (
            <div className="cert-modal__error" style={{ marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <OnboardingForm
            initialValues={{
              firstName: user?.firstName || '',
              lastName: user?.lastName || '',
              title: user?.title || '',
              preferredLocale: user?.preferredLocale || ''
            }}
            onSubmit={handleSubmit}
            loading={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
